import type { LucideIcon } from 'lucide-react'
import { Activity, ArrowLeft, Building2, Boxes, KeyRound, LayoutDashboard, Settings2, Sparkles, Users } from 'lucide-react'
import { isPlatformUser, isTenantAdmin, type AdminUser } from '@retink/web-auth'
import type { NavEntry } from '@retink/web-ui'

export type ManagementItem = { to: string; label: string; icon: LucideIcon; end?: boolean }
export type ManagementEntry = ManagementItem | { heading: string }

export interface ManagementContext {
  tenantOrgId?: string | null
}

export interface ManagementRouteScope {
  organizationId: string | null
  tenantId: string | null
}

export function managementRouteScope(pathname: string): ManagementRouteScope {
  const tenantId = pathname.match(/^\/admin\/tenants\/([^/]+)/)?.[1] ?? null
  const organizationId = pathname.match(/^\/admin\/organizations\/([^/]+)/)?.[1] ?? null
  return { organizationId, tenantId }
}

// Each management menu opens with a heading naming its scope — the menu sits
// below the app-rail's Apps/Services groups, so without one it reads as more
// services instead of the record being managed.
function organizationItems(orgId: string, includeFleetBack: boolean): ManagementEntry[] {
  const base = `/admin/organizations/${orgId}`
  return [
    ...(includeFleetBack
      ? [{ to: '/admin/organizations', label: 'All organizations', icon: ArrowLeft, end: true }]
      : []),
    { heading: 'Organization' },
    { to: `${base}/overview`, label: 'Overview', icon: LayoutDashboard },
    { to: `${base}/people`, label: 'People', icon: Users },
    { to: `${base}/identity`, label: 'Sign-in', icon: KeyRound },
    { to: `${base}/tenants`, label: 'Tenants', icon: Boxes },
    { to: `${base}/activity`, label: 'Activity', icon: Activity },
  ]
}

function tenantItems(tenantId: string, orgId?: string | null): ManagementEntry[] {
  const base = `/admin/tenants/${tenantId}`
  return [
    {
      to: orgId ? `/admin/organizations/${orgId}/tenants` : '/admin/tenants',
      label: orgId ? 'Organization tenants' : 'All tenants',
      icon: ArrowLeft,
      end: true,
    },
    { heading: 'Tenant' },
    { to: `${base}/overview`, label: 'Overview', icon: LayoutDashboard },
    { to: `${base}/people`, label: 'People', icon: Users },
    { to: `${base}/sign-in`, label: 'Sign-in', icon: KeyRound },
    { to: `${base}/products`, label: 'Products', icon: Boxes },
    { to: `${base}/features`, label: 'Features', icon: Settings2 },
    { to: `${base}/adapters`, label: 'Adapters', icon: Settings2 },
    { to: `${base}/ai`, label: 'AI', icon: Sparkles },
  ]
}

/** Visible management destinations. Server middleware remains authoritative. */
export function managementItems(
  user: AdminUser,
  pathname = '/admin',
  context: ManagementContext = {},
): ManagementEntry[] {
  // Acting inside a tenant, Administration IS that tenant: the menu holds only
  // tenant destinations, whoever you are. Organization and platform management
  // belong to the exited state — the shell's "Exit tenant" is the one road
  // there — so their menus never frame tenant work.
  if (user.tenantId) {
    return isTenantAdmin(user)
      ? [
          { heading: 'Tenant' },
          { to: '/admin/tenant', label: 'Overview', icon: LayoutDashboard, end: true },
          { to: '/admin/tenant/team', label: 'People', icon: Users },
          { to: '/admin/tenant/ai', label: 'AI & Models', icon: Sparkles },
        ]
      : []
  }

  if (isPlatformUser(user)) {
    const { organizationId, tenantId: recordTenantId } = managementRouteScope(pathname)
    if (recordTenantId) return tenantItems(recordTenantId, context.tenantOrgId)
    if (organizationId) return organizationItems(organizationId, true)

    return [
      { heading: 'Customer estate' },
      { to: '/admin/organizations', label: 'Organizations', icon: Building2 },
      { to: '/admin/tenants', label: 'Tenants', icon: Boxes, end: true },
      { to: '/admin/identities', label: 'Identities', icon: Users },
      { heading: 'Platform operations' },
      { to: '/admin/staff', label: 'Staff', icon: Users },
      { to: '/admin/services', label: 'Services', icon: Boxes },
      { to: '/admin/identity-providers', label: 'Identity providers', icon: KeyRound },
      { to: '/admin/activity', label: 'Activity', icon: Activity },
    ]
  }

  if (user.orgRole === 'admin' && user.orgId) {
    return organizationItems(user.orgId, false)
  }

  return []
}

export function managementLabel(pathname: string, items: NavEntry[]): string | null {
  const current = items
    .filter((item): item is ManagementItem => 'to' in item)
    .filter((item) => pathname === item.to || (!item.end && pathname.startsWith(`${item.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0]
  return current?.label ?? (pathname.startsWith('/admin/') ? 'Manage' : null)
}
